<script lang="ts" setup>
import { parse, format } from 'date-fns'

const props = withDefaults(
  defineProps<{
    showFallbacks?: boolean
    showWatermark?: boolean
    restrictWidth?: boolean
    mode?: 'screen' | 'print'
  }>(),
  {
    showFallbacks: false,
    showWatermark: false,
    restrictWidth: true,
    mode: 'screen',
  },
)

const aggregateStepsStore = useAggregateStepsStore()
const fieldFocusManagerStore = useFieldFocusManagerStore()
const options = useOptions()

function parseDate(date: string) {
  const parsedDate = parse(date, 'yyyy-MM-dd', new Date())
  if (isNaN(parsedDate.getTime())) return undefined
  return parsedDate
}

function formatDate(date: Date | undefined) {
  if (!date) return ''
  return format(date, 'dd.MM.yyyy')
}

const birthdate = computed(() =>
  parseDate(aggregateStepsStore.steps.gestalten.verstorbenerGeburtsdatum.liveValue),
)
const deathdate = computed(() =>
  parseDate(aggregateStepsStore.steps.gestalten.verstorbenerTodesdatum.liveValue),
)
const birthdateFormatted = computed(() => formatDate(birthdate.value))
const deathdateFormatted = computed(() => formatDate(deathdate.value))

const imageUrl = computed(() =>
  aggregateStepsStore.steps.gestalten.noImage.liveValue
    ? undefined
    : !aggregateStepsStore.steps.gestalten.sujet.isEmpty
      ? `${aggregateStepsStore.steps.gestalten.sujet.url}`
      : `${options.pluginDirUrl}subjects/${aggregateStepsStore.steps.gestalten.sujetVorauswahl.liveValue.file}`,
)

const backgroundCssUrl = computed(() =>
  !aggregateStepsStore.steps.gestalten.hintergrund.isEmpty
    ? `url(${aggregateStepsStore.steps.gestalten.hintergrund.url})`
    : `url(${options.pluginDirUrl}backgrounds/${aggregateStepsStore.steps.gestalten.hintergrundVorauswahl.liveValue.file})`,
)
</script>

<template>
  <div class="relative z-50 w-full @container" :class="{ 'max-w-[640px]': restrictWidth }">
    <div
      class="announcement flex min-h-[400px] flex-col justify-center gap-4 bg-[var(--bg-color)] px-8 py-20 pb-[max(5rem,var(--additional-padding))] [font-family:var(--font)] @md:px-12 @lg:px-16 s:min-h-[500px] sm:min-h-[600px]"
      :class="{
        'text-black': !aggregateStepsStore.steps.gestalten.farbe.liveValue.isDark,
        'text-white': aggregateStepsStore.steps.gestalten.farbe.liveValue.isDark,
      }"
      :style="{
        '--focus-marker-color': aggregateStepsStore.steps.gestalten.farbe.liveValue.isDark
          ? 'rgba(210 161 38 / 80%)'
          : 'rgba(245 245 0 / 80%)',
        '--font': `'${aggregateStepsStore.steps.gestalten.schriftart.liveValue.key}', sans-serif`,
        '--bg-color': aggregateStepsStore.steps.gestalten.farbe.liveValue.hex,
        '--bg-image': backgroundCssUrl,
        '--bg-size': !aggregateStepsStore.steps.gestalten.hintergrund.isEmpty
          ? 'cover'
          : aggregateStepsStore.steps.gestalten.hintergrundVorauswahl.liveValue.size,
        '--bg-position': !aggregateStepsStore.steps.gestalten.hintergrund.isEmpty
          ? 'center'
          : aggregateStepsStore.steps.gestalten.hintergrundVorauswahl.liveValue.position,
        '--bg-opacity': !aggregateStepsStore.steps.gestalten.hintergrund.isEmpty
          ? 0.35
          : aggregateStepsStore.steps.gestalten.hintergrundVorauswahl.liveValue.opacity,
        '--subject-opacity': !aggregateStepsStore.steps.gestalten.sujet.isEmpty
          ? 1
          : aggregateStepsStore.steps.gestalten.sujetVorauswahl.liveValue.opacity,
        '--additional-padding': !aggregateStepsStore.steps.gestalten.hintergrund.isEmpty
          ? '0px'
          : `${Math.round(aggregateStepsStore.steps.gestalten.hintergrundVorauswahl.liveValue.additionalPadding * 100)}%`,
        '--fallback-opacity': '0.4',
      }"
    >
      <div
        class="pointer-events-none absolute inset-0 bg-no-repeat opacity-[var(--bg-opacity)] [background-image:var(--bg-image)] [background-position:var(--bg-position)] [background-size:var(--bg-size)]"
        :class="
          !aggregateStepsStore.steps.gestalten.hintergrund.isEmpty
            ? undefined
            : {
                invert:
                  aggregateStepsStore.steps.gestalten.hintergrundVorauswahl.liveValue.blend &&
                  aggregateStepsStore.steps.gestalten.farbe.liveValue.isDark,
                'mix-blend-multiply':
                  aggregateStepsStore.steps.gestalten.hintergrundVorauswahl.liveValue.blend &&
                  !aggregateStepsStore.steps.gestalten.farbe.liveValue.isDark,
                'mix-blend-screen':
                  aggregateStepsStore.steps.gestalten.hintergrundVorauswahl.liveValue.blend &&
                  aggregateStepsStore.steps.gestalten.farbe.liveValue.isDark,
              }
        "
      />
      <div
        v-if="showWatermark"
        class="pointer-events-none absolute left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-45 select-none font-copy text-[22.5cqmin] font-bold"
        :class="{
          'opacity-[3%]': aggregateStepsStore.steps.gestalten.hintergrund.isEmpty,
          'opacity-[10%]': !aggregateStepsStore.steps.gestalten.hintergrund.isEmpty,
        }"
      >
        Vorschau
      </div>

      <div class="relative">
        <div class="flex flex-col gap-4">
          <!-- Ort, Datum -->
          <Prose
            v-if="aggregateStepsStore.steps.gestalten.ortUndDatum.liveValue || showFallbacks"
            class="overflow-hidden"
            :class="{
              '[&>*]:opacity-[var(--fallback-opacity)]':
                !aggregateStepsStore.steps.gestalten.ortUndDatum.liveValue,
              'focus-marker': fieldFocusManagerStore.field === 'ortUndDatum',
            }"
          >
            <div
              class="trim-spacings text-s"
              v-html="
                aggregateStepsStore.steps.gestalten.ortUndDatum.liveValue || '<p>Ort, Datum</p>'
              "
            />
          </Prose>

          <!-- Trauerspruch -->
          <Prose
            v-if="aggregateStepsStore.steps.gestalten.trauerspruch.liveValue || showFallbacks"
            class="overflow-hidden"
            :class="{
              '[&>*]:opacity-[var(--fallback-opacity)]':
                !aggregateStepsStore.steps.gestalten.trauerspruch.liveValue,
              'focus-marker': fieldFocusManagerStore.field === 'trauerspruch',
            }"
          >
            <div
              class="trim-spacings text-md"
              v-html="
                aggregateStepsStore.steps.gestalten.trauerspruch.liveValue || '<p>Trauerspruch</p>'
              "
            />
          </Prose>

          <!-- Abschiedsformulierung -->
          <Prose
            v-if="
              aggregateStepsStore.steps.gestalten.abschiedsformulierung.liveValue || showFallbacks
            "
            class="overflow-hidden"
            :class="{
              '[&>*]:opacity-[var(--fallback-opacity)]':
                !aggregateStepsStore.steps.gestalten.abschiedsformulierung.liveValue,
              'focus-marker': fieldFocusManagerStore.field === 'abschiedsformulierung',
            }"
          >
            <div
              class="trim-spacings text-md"
              v-html="
                aggregateStepsStore.steps.gestalten.abschiedsformulierung.liveValue ||
                '<p>Abschiedsformulierung</p>'
              "
            />
          </Prose>
        </div>
        <!-- Subject image, name, dates -->
        <Prose class="overflow-hidden">
          <div class="text-center">
            <template v-if="!aggregateStepsStore.steps.gestalten.noImage.liveValue">
              <img
                :src="imageUrl"
                alt=""
                class="mx-auto mb-4 mt-7 block size-[180px] select-none object-contain opacity-[var(--subject-opacity)]"
                :class="
                  !aggregateStepsStore.steps.gestalten.sujet.isEmpty
                    ? {
                        'object-cover':
                          aggregateStepsStore.steps.gestalten.sujetCrop.liveValue !== 'none',
                        'rounded-full':
                          aggregateStepsStore.steps.gestalten.sujetCrop.liveValue === 'circle',
                      }
                    : {
                        invert:
                          aggregateStepsStore.steps.gestalten.sujetVorauswahl.liveValue.blend &&
                          aggregateStepsStore.steps.gestalten.farbe.liveValue.isDark,
                        'mix-blend-multiply':
                          aggregateStepsStore.steps.gestalten.sujetVorauswahl.liveValue.blend &&
                          !aggregateStepsStore.steps.gestalten.farbe.liveValue.isDark,
                        'mix-blend-screen':
                          aggregateStepsStore.steps.gestalten.sujetVorauswahl.liveValue.blend &&
                          aggregateStepsStore.steps.gestalten.farbe.liveValue.isDark,
                      }
                "
                draggable="false"
              />
            </template>
            <div role="heading" aria-level="1" class="text-2xl font-bold leading-none">
              <template
                v-if="
                  aggregateStepsStore.steps.gestalten.verstorbenerVorname.liveValue &&
                  aggregateStepsStore.steps.gestalten.verstorbenerNachname.liveValue
                "
              >
                <span
                  :class="{
                    'focus-marker': fieldFocusManagerStore.field === 'verstorbenerVorname',
                  }"
                >
                  {{ aggregateStepsStore.steps.gestalten.verstorbenerVorname.liveValue }} </span
                >{{ ' ' }}
                <span
                  :class="{
                    'focus-marker': fieldFocusManagerStore.field === 'verstorbenerNachname',
                  }"
                >
                  {{ aggregateStepsStore.steps.gestalten.verstorbenerNachname.liveValue }}
                </span>
              </template>
              <template v-else>
                <span
                  class="[&>*]:opacity-[var(--fallback-opacity)]"
                  :class="{
                    'focus-marker': fieldFocusManagerStore.field === 'verstorbenerVorname',
                  }"
                >
                  <span>Vorname</span>
                </span>
                {{ ' ' }}
                <span
                  class="[&>*]:opacity-[var(--fallback-opacity)]"
                  :class="{
                    'focus-marker': fieldFocusManagerStore.field === 'verstorbenerNachname',
                  }"
                >
                  <span>Nachname</span>
                </span>
              </template>
            </div>
            <p
              v-if="aggregateStepsStore.steps.gestalten.verstorbenerLedigerNachname.liveValue"
              class="mt-1"
            >
              geb.
              <span
                :class="{
                  'focus-marker': fieldFocusManagerStore.field === 'verstorbenerLedigerNachname',
                }"
              >
                {{ aggregateStepsStore.steps.gestalten.verstorbenerLedigerNachname.liveValue }}
              </span>
            </p>
            <p
              v-else-if="showFallbacks"
              class="mt-1 [&_[data-fallback]]:opacity-[var(--fallback-opacity)]"
            >
              <span data-fallback>geb. </span>
              <span
                :class="{
                  'focus-marker': fieldFocusManagerStore.field === 'verstorbenerLedigerNachname',
                }"
              >
                <span data-fallback>Nachname</span>
              </span>
            </p>
            <p class="mt-2 text-s">
              <template v-if="birthdate && deathdate">
                <span
                  :class="{
                    'focus-marker': fieldFocusManagerStore.field === 'verstorbenerGeburtsdatum',
                  }"
                >
                  {{ birthdateFormatted }}
                </span>
                -
                <span
                  :class="{
                    'focus-marker': fieldFocusManagerStore.field === 'verstorbenerTodesdatum',
                  }"
                >
                  {{ deathdateFormatted }}
                </span>
              </template>
              <template v-else>
                <span
                  :class="{
                    'focus-marker': fieldFocusManagerStore.field === 'verstorbenerGeburtsdatum',
                  }"
                >
                  01.01.1900
                </span>
                -
                <span
                  :class="{
                    'focus-marker': fieldFocusManagerStore.field === 'verstorbenerTodesdatum',
                  }"
                >
                  01.01.2000
                </span>
              </template>
            </p>
          </div>
        </Prose>
        <div class="flex flex-col gap-4">
          <!-- Haupttext -->
          <Prose
            v-if="aggregateStepsStore.steps.gestalten.haupttext.liveValue || showFallbacks"
            class="overflow-hidden"
            :class="{
              '[&>*]:opacity-[var(--fallback-opacity)]':
                !aggregateStepsStore.steps.gestalten.haupttext.liveValue,
              'focus-marker': fieldFocusManagerStore.field === 'haupttext',
            }"
          >
            <div
              class="trim-spacings text-sm"
              v-html="aggregateStepsStore.steps.gestalten.haupttext.liveValue || '<p>Haupttext</p>'"
            />
          </Prose>
          <!-- Angehörige -->
          <div
            class="grid gap-8"
            :class="{
              '@md:grid-cols-2':
                aggregateStepsStore.steps.gestalten.angehoerige2.liveValue || showFallbacks,
            }"
          >
            <div>
              <Prose
                v-if="aggregateStepsStore.steps.gestalten.angehoerige.liveValue || showFallbacks"
                class="overflow-hidden"
                :class="{
                  '[&>*]:opacity-[var(--fallback-opacity)]':
                    !aggregateStepsStore.steps.gestalten.angehoerige.liveValue,
                  'focus-marker': fieldFocusManagerStore.field === 'angehoerige',
                }"
              >
                <div
                  class="trim-spacings text-sm"
                  v-html="
                    aggregateStepsStore.steps.gestalten.angehoerige.liveValue ||
                    '<p>Angehörige (Spalte 1)</p><p><small>Wird nur eine Spalte verwendet, erscheinen die Angehörigen im nächsten Schritt automatisch einspaltig über die gesamte Breite.</small></p>'
                  "
                />
              </Prose>
            </div>
            <div v-if="aggregateStepsStore.steps.gestalten.angehoerige2.liveValue || showFallbacks">
              <Prose
                class="overflow-hidden"
                :class="{
                  '[&>*]:opacity-[var(--fallback-opacity)]':
                    !aggregateStepsStore.steps.gestalten.angehoerige2.liveValue,
                  'focus-marker': fieldFocusManagerStore.field === 'angehoerige2',
                }"
              >
                <div
                  class="trim-spacings text-sm"
                  v-html="
                    aggregateStepsStore.steps.gestalten.angehoerige2.liveValue ||
                    '<p>Angehörige (Spalte 2)</p>'
                  "
                />
              </Prose>
            </div>
          </div>

          <!-- Trauerfeier -->
          <Prose
            v-if="aggregateStepsStore.steps.gestalten.trauerfeier.liveValue || showFallbacks"
            class="overflow-hidden"
            :class="{
              '[&>*]:opacity-[var(--fallback-opacity)]':
                !aggregateStepsStore.steps.gestalten.trauerfeier.liveValue,
              'focus-marker': fieldFocusManagerStore.field === 'trauerfeier',
            }"
          >
            <div
              class="trim-spacings text-sm"
              v-html="
                aggregateStepsStore.steps.gestalten.trauerfeier.liveValue || '<p>Trauerfeier</p>'
              "
            />
          </Prose>
          <!-- Traueradresse -->
          <Prose
            v-if="aggregateStepsStore.steps.gestalten.traueradresse.liveValue || showFallbacks"
            class="overflow-hidden"
            :class="{
              '[&>*]:opacity-[var(--fallback-opacity)]':
                !aggregateStepsStore.steps.gestalten.traueradresse.liveValue,
              'focus-marker': fieldFocusManagerStore.field === 'traueradresse',
            }"
          >
            <div
              class="trim-spacings text-s"
              v-html="
                aggregateStepsStore.steps.gestalten.traueradresse.liveValue ||
                '<p>Traueradresse</p>'
              "
            />
          </Prose>
          <!-- Spenden -->
          <Prose
            v-if="aggregateStepsStore.steps.gestalten.spenden.liveValue || showFallbacks"
            class="overflow-hidden"
            :class="{
              '[&>*]:opacity-[var(--fallback-opacity)]':
                !aggregateStepsStore.steps.gestalten.spenden.liveValue,
              'focus-marker': fieldFocusManagerStore.field === 'spenden',
            }"
          >
            <div
              class="trim-spacings text-s"
              v-html="aggregateStepsStore.steps.gestalten.spenden.liveValue || '<p>Spenden</p>'"
            />
          </Prose>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.announcement :deep(p:not([class])) {
  margin-block: 0.5em;
}
</style>
